import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { ArrowLeft, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";

type UnsubscribeSearch = {
  token?: string;
};

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: (search: Record<string, unknown>): UnsubscribeSearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const unsubscribe = useMutation(api.waitlist.unsubscribe);
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");

  const handleUnsubscribe = async () => {
    if (!token || status === "loading") return;
    setStatus("loading");

    try {
      const result = await unsubscribe({ token });
      setStatus(result.success ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  const hasValidToken = Boolean(token && token.length >= 24);

  return (
    <main className="min-h-screen bg-[#f2f3fa] px-4 py-10 sm:py-16">
      <div className="mx-auto w-full max-w-xl">
        <a href="/join" className="inline-flex items-center" aria-label="suuper">
          <img src="/suuper_logo.svg" alt="suuper" className="h-8 w-auto" />
        </a>

        <section className="mt-10 overflow-hidden rounded-[32px] border border-border bg-white shadow-[0_24px_70px_rgba(22,24,45,0.10)]">
          <div className="h-2 bg-primary-gradient" />
          <div className="p-7 sm:p-10">
            {status === "success" ? (
              <>
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <CheckCircle size={30} weight="fill" />
                </span>
                <h1 className="mt-7 text-3xl font-bold tracking-tight text-text sm:text-4xl">
                  Te-am dezabonat.
                </h1>
                <p className="mt-4 text-lg leading-relaxed text-text-light">
                  Nu vei mai primi emailuri despre lansare, praguri sau prețuri
                  noi din waitlist.
                </p>
              </>
            ) : (
              <>
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <EnvelopeSimple size={28} weight="duotone" />
                </span>
                <p className="mt-7 text-[11px] font-bold tracking-[0.14em] text-primary">
                  PREFERINȚE EMAIL
                </p>
                <h1 className="mt-3 text-3xl font-bold tracking-tight text-text sm:text-4xl">
                  Nu mai vrei emailuri de la noi?
                </h1>
                <p className="mt-4 text-lg leading-relaxed text-text-light">
                  Confirmă mai jos și oprim mesajele despre lansarea suuper și
                  noile praguri ale comunității.
                </p>

                {!hasValidToken || status === "error" ? (
                  <div className="mt-7 rounded-2xl bg-error/10 p-4 text-sm font-medium text-error">
                    Linkul nu este valid sau a expirat. Scrie-ne la
                    support@suuper.ro și rezolvăm imediat.
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleUnsubscribe}
                    disabled={status === "loading"}
                    className="join-cta mt-8 w-full rounded-2xl bg-primary-gradient px-6 py-4 text-base font-semibold text-white shadow-lg shadow-primary/20 disabled:cursor-wait disabled:opacity-60"
                  >
                    {status === "loading"
                      ? "Se actualizează…"
                      : "Confirmă dezabonarea"}
                  </button>
                )}
              </>
            )}

            <a
              href="/join"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-text-light transition-colors hover:text-primary"
            >
              <ArrowLeft size={17} weight="bold" />
              Înapoi la suuper
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}
